import React, { useState, useCallback } from "react";
import { Edit2, Trash2, ChevronDown, ChevronRight, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import { useBoundStore } from "../../store";
import { useI18n } from "../../i18n";
import { Rule, SourceLocation } from "../../services/Playbook";

interface RuleItemProps {
  rule: Rule;
  playbookId: string;
  canEdit: boolean;
  onHover?: (isHovered: boolean) => void;
  isHovered?: boolean;
  onSourceHover?: (source: SourceLocation | null) => void;
  onSourceClick?: (source: SourceLocation) => void;
}

export default function RuleItem({ rule, playbookId, canEdit, onHover, isHovered, onSourceHover, onSourceClick }: RuleItemProps) {
  const { t } = useI18n();
  const updateRule = useBoundStore((state) => state.updateRule);
  const deleteRule = useBoundStore((state) => state.deleteRule);
  const isLoading = useBoundStore((state) => state.isLoading);

  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(rule.name);
  const [editInstructions, setEditInstructions] = useState(rule.instructions);
  const [editComments, setEditComments] = useState(rule.comments || "");
  const [editEnableComments, setEditEnableComments] = useState(rule.enableComments);
  const [editIsActive, setEditIsActive] = useState(rule.isActive);

  const handleSaveEdit = useCallback(async () => {
    if (!editName.trim() || !editInstructions.trim()) {
      toast.error(t.playbooks.ruleItem.nameAndInstructionsRequired);
      return;
    }

    const updated = await updateRule(playbookId, rule.id, {
      name: editName.trim(),
      instructions: editInstructions.trim(),
      comments: editComments.trim() || undefined,
      enableComments: editEnableComments,
      isActive: editIsActive,
    });

    if (updated) {
      toast.success(t.playbooks.ruleItem.updateSuccess);
      setIsEditing(false);
    } else {
      toast.error(t.playbooks.ruleItem.updateFailed);
    }
  }, [
    playbookId,
    rule.id,
    editName,
    editInstructions,
    editComments,
    editEnableComments,
    editIsActive,
    updateRule,
    t,
  ]);

  const handleCancelEdit = useCallback(() => {
    setEditName(rule.name);
    setEditInstructions(rule.instructions);
    setEditComments(rule.comments || "");
    setEditEnableComments(rule.enableComments);
    setEditIsActive(rule.isActive);
    setIsEditing(false);
  }, [rule]);

  const handleDelete = useCallback(async () => {
    const confirmed = window.confirm(t.playbooks.ruleItem.deleteConfirm);
    if (!confirmed) return;

    const updated = await deleteRule(playbookId, rule.id);
    if (updated) {
      toast.success(t.playbooks.ruleItem.deleteSuccess);
    } else {
      toast.error(t.playbooks.ruleItem.deleteFailed);
    }
  }, [playbookId, rule.id, deleteRule, t]);

  const handleToggleActive = useCallback(
    async (checked: boolean) => {
      const updated = await updateRule(playbookId, rule.id, {
        isActive: checked,
      });

      if (updated) {
        toast.success(checked ? t.playbooks.ruleItem.activated : t.playbooks.ruleItem.deactivated);
      } else {
        toast.error(t.playbooks.ruleItem.updateFailed);
      }
    },
    [playbookId, rule.id, updateRule, t]
  );

  const hasSourceQuotes = rule.sourceLocations && rule.sourceLocations.length > 0;

  return (
    <Card
      className={`transition-all ${isHovered ? 'ring-2 ring-yellow-400 shadow-lg' : ''}`}
      onMouseEnter={() => onHover?.(true)}
      onMouseLeave={() => onHover?.(false)}
    >
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-between p-4">
          <CollapsibleTrigger asChild>
            <div className="flex items-center gap-3 flex-1 pr-4 cursor-pointer hover:bg-muted/50 rounded-md -m-2 p-2 transition-colors">
              <div className="p-0 h-auto">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-semibold break-words">{rule.name}</h4>
                  {!rule.isActive && <Badge variant="secondary">{t.playbooks.ruleItem.inactive}</Badge>}
                  {rule.enableComments && (
                    <Badge variant="outline" className="gap-1">
                      <MessageSquare className="h-3 w-3" />
                      {t.playbooks.ruleItem.comments}
                    </Badge>
                  )}
                  {hasSourceQuotes && (
                    <Badge variant="outline" className="bg-yellow-50">
                      {t.playbooks.ruleItem.sources(rule.sourceLocations!.length)}
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground break-words">{rule.instructions}</p>
              </div>
            </div>
          </CollapsibleTrigger>

          {canEdit && !isEditing && (
            <div className="flex items-center gap-2">
              <Switch checked={rule.isActive} onCheckedChange={handleToggleActive} disabled={isLoading} />
              <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                <Edit2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          )}
        </div>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor={`rule-name-${rule.id}`}>{t.playbooks.ruleItem.nameLabel}</Label>
                  <Input
                    id={`rule-name-${rule.id}`}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder={t.playbooks.ruleItem.namePlaceholder}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor={`rule-instructions-${rule.id}`}>{t.playbooks.ruleItem.instructionsLabel}</Label>
                  <Textarea
                    id={`rule-instructions-${rule.id}`}
                    value={editInstructions}
                    onChange={(e) => setEditInstructions(e.target.value)}
                    placeholder={t.playbooks.ruleItem.instructionsPlaceholder}
                    rows={4}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor={`rule-enable-comments-${rule.id}`}>{t.playbooks.ruleItem.enableCommentsLabel}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t.playbooks.ruleItem.enableCommentsHint}
                    </p>
                  </div>
                  <Switch
                    id={`rule-enable-comments-${rule.id}`}
                    checked={editEnableComments}
                    onCheckedChange={setEditEnableComments}
                  />
                </div>

                {editEnableComments && (
                  <div className="grid gap-2">
                    <Label htmlFor={`rule-comments-${rule.id}`}>
                      {t.playbooks.ruleItem.commentGuidelinesLabel}
                    </Label>
                    <Textarea
                      id={`rule-comments-${rule.id}`}
                      value={editComments}
                      onChange={(e) => setEditComments(e.target.value)}
                      placeholder={t.playbooks.ruleItem.commentGuidelinesPlaceholder}
                      rows={3}
                    />
                  </div>
                )}

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor={`rule-is-active-${rule.id}`}>{t.playbooks.ruleItem.activeLabel}</Label>
                    <p className="text-sm text-muted-foreground">
                      {t.playbooks.ruleItem.activeHint}
                    </p>
                  </div>
                  <Switch
                    id={`rule-is-active-${rule.id}`}
                    checked={editIsActive}
                    onCheckedChange={setEditIsActive}
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleSaveEdit} disabled={isLoading}>
                    {t.playbooks.ruleItem.saveChanges}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEdit} disabled={isLoading}>
                    {t.playbooks.ruleItem.cancel}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <h5 className="text-sm font-medium mb-2">{t.playbooks.ruleItem.instructionsHeading}</h5>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                    {rule.instructions}
                  </p>
                </div>

                {rule.enableComments && rule.comments && (
                  <div>
                    <h5 className="text-sm font-medium mb-2">{t.playbooks.ruleItem.commentGuidelinesHeading}</h5>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">{rule.comments}</p>
                  </div>
                )}

                {hasSourceQuotes && (
                  <div>
                    <h5 className="text-sm font-medium mb-2">{t.playbooks.ruleItem.sourceQuotesHeading}</h5>
                    <div className="space-y-2">
                      {rule.sourceLocations!.map((source, idx) => (
                        <div
                          key={idx}
                          className="bg-yellow-50 border border-yellow-200 rounded p-3 cursor-pointer hover:bg-yellow-100 hover:border-yellow-300 transition-colors"
                          onMouseEnter={() => onSourceHover?.(source)}
                          onMouseLeave={() => onSourceHover?.(null)}
                          onClick={() => onSourceClick?.(source)}
                        >
                          <p className="text-sm italic text-gray-700">"{source.quote}"</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            {source.location.paragraphIndex !== undefined && (
                              <span>{t.playbooks.ruleItem.paragraph(source.location.paragraphIndex + 1)}</span>
                            )}
                            {source.location.page !== undefined && (
                              <span>{t.playbooks.ruleItem.page(source.location.page)}</span>
                            )}
                            {source.location.sheet && source.location.row && source.location.column && (
                              <span>{t.playbooks.ruleItem.sheetCell(source.location.sheet, `${source.location.column}${source.location.row}`)}</span>
                            )}
                            {source.confidence && (
                              <Badge variant="outline" className="ml-auto text-xs">
                                {t.playbooks.ruleItem.confidence(source.confidence)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                  <span>{t.playbooks.ruleItem.created(new Date(rule.createdAt).toLocaleDateString())}</span>
                  <span>{t.playbooks.ruleItem.updated(new Date(rule.updatedAt).toLocaleDateString())}</span>
                </div>
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
